import { Callout, Card, Flex, Text, Title } from '@tremor/react';
import { ConfirmDeletionModal } from '@/components/layouts/Modals/ConfirmDeletionModal/ConfirmDeletionModal';
import { Details, DetailsProps } from './Details';
import { ConnectorCardReducerState } from '@/types/reducers';
import { connectorsWithPngIcons, NAV_ROUTES } from '@/utils/constants';
import { ValidConnector } from '@/types/Connectors';
import { useNavigate } from 'react-router-dom';
import { ConnectorCardBadge } from './ConnectorCardBadge';
import { Edit3, Trash2 } from 'lucide-react';
import { hasPermission } from '@/auth/hasPermission';
import Switch from '@/components/layouts/Inputs/Switch/Switch';
import Button from '@/components/layouts/Buttons/Button/Button';

interface Props extends DetailsProps {
  connectorType: string;
  connectorName: string;
  state: ConnectorCardReducerState;
  disabled: boolean;
  workspaceId: string;
  isDeactivationModalOpen: boolean;
  isDeletionnModalOpen: boolean;
  closeDeactivationModal: () => void;
  closeDeletionModal: () => void;
  changeConnectorStatus: (isActive: boolean) => Promise<void>;
  handleClickDelete: () => void;
  deleteConnector: () => Promise<void>;
  deactivateConnector: () => Promise<void>;
}

export function ConnectorCardView({
  createdBy,
  createdDate,
  lastModifiedBy,
  lastModifiedDate,
  connectorName,
  connectorType,
  state,
  changeConnectorStatus,
  disabled,
  workspaceId,
  closeDeactivationModal,
  closeDeletionModal,
  handleClickDelete,
  isDeactivationModalOpen,
  isDeletionnModalOpen,
  deactivateConnector,
  deleteConnector,
}: Props) {
  const navigate = useNavigate();
  const hasPermissionToDeleteConnector = hasPermission(
    'workspace',
    'connector',
    'delete',
  );
  const hasPermissionToEditConnector = hasPermission('workspace', 'connector', 'update', {
    connectorId: connectorType,
  });

  const deactivationMessage = (
    <>
      <Text>
        Are you sure you want to deactivate this connector? When deactivated, it will no
        longer collect data.
      </Text>
      <Callout style={{ margin: '1rem 0' }} title="Long time opertaion!" color="yellow">
        This action may take a while to complete.
      </Callout>
    </>
  );

  const isDeleteButtonDisabled = disabled || !hasPermissionToDeleteConnector;
  const isConnectorEditionDisabled = disabled || !hasPermissionToEditConnector;

  const noEditPermissionTooltip = !hasPermissionToEditConnector
    ? "You don't have permissions required to edit connector"
    : undefined;

  const noDeletePermissionTooltip = !hasPermissionToDeleteConnector
    ? `You don't have permissions required to delete connector`
    : undefined;

  return (
    <>
      <Card className="bg-white rounded-xl border-gray-200 p-6 hover:shadow-lg transition-all duration-200">
        <Flex flexDirection="col" alignItems="start" className="gap-5">
          <Flex justifyContent="start" alignItems="center">
            <img
              src={`/connectors/${connectorType}.${
                connectorsWithPngIcons.includes(connectorType) ? 'png' : 'svg'
              }`}
              style={{ height: '24px' }}
              alt={`${connectorType} icon`}
            />
            <Title className="ml-[10px] text-[24px] leading-[36px] font-bold capitalize text-secondary-500-base font-inter">
              {connectorName}
            </Title>
            <ConnectorCardBadge state={state} />
          </Flex>

          <Details
            createdBy={createdBy}
            createdDate={createdDate}
            lastModifiedBy={lastModifiedBy}
            lastModifiedDate={lastModifiedDate}
          />

          <Flex className="items-center justify-between pt-4 border-t border-gray-100">
            <Flex className="justify-start space-x-3">
              <Text className="text-sm font-bold text-gray-700">
                {state.isActive ? 'Active' : 'Inactive'}
              </Text>
              <Switch
                disabled={isConnectorEditionDisabled}
                tooltip={noEditPermissionTooltip}
                checked={state.isActive}
                onCheckedChange={async (e) => {
                  await changeConnectorStatus(e);
                }}
              />
            </Flex>
            <Flex className="justify-end space-x-3">
              <Button
                disabled={isConnectorEditionDisabled}
                onClick={() => {
                  navigate(
                    `${NAV_ROUTES.connectorEdit(
                      connectorType.replace('_', '-') as ValidConnector,
                    )}?workspaceId=${workspaceId}`,
                  );
                }}
                tooltip={noEditPermissionTooltip}
                className="border-none shadow-none bg-transparent p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Edit3 className="w-4 h-4" />
              </Button>
              <Button
                className="border-none shadow-none bg-transparent p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                disabled={isDeleteButtonDisabled}
                tooltip={noDeletePermissionTooltip}
                onClick={handleClickDelete}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Card>
      <ConfirmDeletionModal
        isOpen={isDeactivationModalOpen}
        setClose={closeDeactivationModal}
        message={deactivationMessage}
        onConfirm={deactivateConnector}
        title="Deactivate Connector?"
      />
      <ConfirmDeletionModal
        isOpen={isDeletionnModalOpen}
        setClose={closeDeletionModal}
        message="Are you sure you want to delete this connector? This action cannot be undone and will remove all associated data."
        onConfirm={deleteConnector}
        title="Delete Connector?"
      />
    </>
  );
}
